window.dataLayer = window.dataLayer || [];

(() => {
  /**
   * @typedef {Object} DataLayerEvent
   * @property {string} event
   * @property {any} [data]
   * @property {string} [gateway_status]
   */

  class DiageoDatalayerQueue {
    constructor() {
      /** @type {DataLayerEvent[]} */
      this.queue = [];
      this.formatters = Object.create(null);
      /** @type {Set<string>} */
      this._sentEvents = new Set();
      this._pageViewSent = false; // NEW: single source of truth — has a page_view gone out yet
      this._pageViewGivenUp = false; // NEW: set only when page_view/page_viewed never showed up at all
      // Raw (pre-formatter) event names that must never reach window.dataLayer
      // ahead of page_view — these are the events whose formatted output is
      // view_item_list or search. Gated inside sendEvent() itself (see below)
      // rather than at the queue/flush level, so it holds regardless of which
      // path (timeout fallback, normal flush, or a reentrant call triggered by
      // window.dataLayer.push itself) tries to send them early.
      this._heldUntilPageViewEvents = new Set(['collection_viewed', 'search_submitted', 'search', 'view_item_list']);
    }

    /**
     * @param {DataLayerEvent} e
     */
    applyFormatter(e) {
      if (!e?.event) return;

      const formatter = this.formatters[e.event];
      if (!formatter) return e;

      return formatter({ eventObj: e.data });
    }

    /**
     * @param {DataLayerEvent} e
     */
    push(e) {
      const key = e?.event ? `${e.event}:${e.data?.timestamp}` : null;
      if (key && this._sentEvents.has(key)) return;
      this.queue.push(e);
      this.processQueue();
    }

    processQueue() {
      if (window.gtmId && !window.dataLayer.some((e) => e.event == 'gtm.js') && !this._gtmTimedOut) {
        if (!this._gtmFallbackTimer) {
          this._gtmFallbackTimer = setTimeout(() => {
            console.warn('[dataLayerQueue] gtm.js did not fire within timeout — flushing anyway');
            this._gtmTimedOut = true;
            this.flushAll(); // sendEvent defers page-view-dependent events internally, see below
          }, 8000);
        }
        return;
      }

      const gatewayResolvedLive = window.dataLayer.some(
        (e) => e.event == 'gateway' && ['Completed', 'Bypass'].includes(e.gateway_status)
      );
      const gatewayPassed = gatewayResolvedLive || this.hasGatewayPassedCookie(); // NEW helper var

      if (!gatewayPassed && !this._gatewayTimedOut) {
        if (!this._gatewayFallbackTimer) {
          this._gatewayFallbackTimer = setTimeout(() => {
            console.warn('[dataLayerQueue] gateway not resolved within timeout — flushing anyway');
            this._gatewayTimedOut = true;
            this.flushAll(); // sendEvent defers page-view-dependent events internally, see below
          }, 8000);
        }
        return;
      }

      if (window.dataLayer.some((e) => e.event == 'page_view')) {
        this._pageViewSent = true; // NEW
        this.queue = this.queue.filter((e) => e.event !== 'page_viewed'); // NEW — drop stale duplicates
        this.flushAll();
        return;
      }

      const pageViewEvent = this.queue.find((e) => e.event == 'page_viewed');
      if (pageViewEvent && !this._pageViewSent) {
        // CHANGED — added !this._pageViewSent
        this.sendEvent(pageViewEvent);
        this.flushAll();
        return;
      }

      if (!this._pageViewTimedOut && !this._pageViewFallbackTimer) {
        this._pageViewFallbackTimer = setTimeout(() => {
          console.warn('[dataLayerQueue] no page_view/page_viewed within timeout — flushing anyway');
          this._pageViewTimedOut = true;
          this._pageViewGivenUp = true; // NEW — page_view genuinely isn't coming, stop holding for it
          this.flushAll();
        }, 8000);
      }
    }

    hasGatewayPassedCookie() {
      return document.cookie.split(';').some((c) => c.trim().startsWith('diageo-gateway='));
    }

    // Snapshot-then-clear: sendEvent() may re-queue an item that's still
    // being held for page_view (see below). Clearing this.queue up front,
    // rather than after the forEach, means a re-queued item lands in the
    // new (now current) queue instead of being wiped out by this method's
    // own cleanup.
    flushAll() {
      const toProcess = this.queue;
      this.queue = [];
      toProcess.forEach((e) => this.sendEvent(e));
    }

    /**
     * @param {DataLayerEvent} e
     */
    sendEvent(e) {
      if (!e?.event) return;

      if (e.event === 'page_viewed' && this._pageViewSent) return; // NEW — hard stop on duplicates

      // Hold view_item_list/search-producing events back until page_view has
      // actually been pushed. Re-queuing (rather than dropping) means the
      // next processQueue() cycle — triggered by the eventual page_viewed
      // arrival, or anything else pushing to dataLayer — will retry it.
      if (this._heldUntilPageViewEvents.has(e.event) && !this._pageViewSent && !this._pageViewGivenUp) {
        this.queue.push(e);
        return;
      }

      let formatedEvent;
      try {
        formatedEvent = this.applyFormatter(e);
      } catch (err) {
        console.error('[dataLayerQueue] formatter failed for', e.event, err);
        return;
      }

      if (formatedEvent) {
        // Guard against the same underlying thing being reported twice — this
        // matters most while the queue is gated behind the age-gateway/consent
        // wait, where duplicate raw events can pile up in this.queue unfiltered
        // (nothing reaches _sentEvents until it's actually sent) and then all
        // get flushed together. Scoped to content, not just event name, so a
        // genuinely different list or a second distinct search term still fires.
        if (formatedEvent.event === 'view_item_list') {
          const listId = formatedEvent.ecommerce?.items?.[0]?.item_list_id;
          const listKey = `view_item_list:${listId}`;
          if (listId !== undefined && this._sentEvents.has(listKey)) return;
          if (listId !== undefined) this._sentEvents.add(listKey);
        }

        if (formatedEvent.event === 'search') {
          const searchKey = `search:${formatedEvent.search_term}`;
          if (formatedEvent.search_term !== undefined && this._sentEvents.has(searchKey)) return;
          if (formatedEvent.search_term !== undefined) this._sentEvents.add(searchKey);
        }

        this.queue = this.queue.filter((_e) => _e.event !== e.event);
        window.dataLayer.push(formatedEvent);

        if (e.event === 'page_viewed') this._pageViewSent = true; // NEW

        const key = e?.event ? `${e.event}:${e.data?.timestamp}` : null;
        if (key) this._sentEvents.add(key);
      }
    }
  }

  const dataLayerQueue = new DiageoDatalayerQueue();

  const originalPush = window.dataLayer.push;

  window.dataLayer.push = function (...args) {
    dataLayerQueue.processQueue();
    return originalPush.apply(this, args);
  };

  window.dataLayerFormatters = (formattersPatch) => {
    if (formattersPatch && typeof formattersPatch === 'object') {
      Object.assign(dataLayerQueue.formatters, formattersPatch);
    }
  };

  window.dataLayerPush = (ctx) => {
    dataLayerQueue.push({ ...ctx });
  };

  window.addEventListener('message', function (e) {
    if (e.data?.source !== 'shopify-pixel' && e.origin == null) return;

    dataLayerQueue.push({ ...e.data });
  });

  document.querySelector('cart-drawer-component')?.addEventListener('dialog:open', async () => {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    if (!cart) return;

    window.dataLayerPush({
      event: 'cart_drawer_viewed',
      data: {
        timestamp: new Date().toISOString(),
        data: {
          cart,
        },
      },
    });
  });

  const resultsList = document.querySelector('#ResultsList');

  resultsList?.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('product-card a');

    if (!link) return;

    const productCard = link.closest('product-card');

    if (!productCard) {
      return;
    }

    const productId = productCard.dataset.productId;

    if (!productId) {
      return;
    }

    if (!productId) return;

    const collectionItemsData = localStorage.getItem('lastViewedCollectionItems');

    const getSelectedItem = (
      /** @type {string | null} */ collectionItemsData,
      /** @type {string | undefined} */ targetId
    ) => {
      if (!collectionItemsData || !targetId) return;

      let parsedData;

      try {
        parsedData = JSON.parse(collectionItemsData);
      } catch {
        return;
      }

      const items = parsedData?.items;

      if (!Array.isArray(items)) return;

      const item = items.find((listItem) => String(listItem?.id) === String(targetId));

      if (!item) return;

      return {
        item,
        currency: parsedData?.currency || undefined,
        value: item?.price || undefined,
      };
    };

    const selectedItem = getSelectedItem(collectionItemsData, productId);

    if (!selectedItem?.item) {
      console.warn('[dataLayer] select_item item not found', {
        productId,
      });
      return;
    }

    window.dataLayerPush({
      event: 'collection_selected_item',
      data: {
        timestamp: new Date().toISOString(),
        data: {
          payload: {
            currency: selectedItem.currency,
            value: selectedItem.value,
            item: selectedItem.item,
          },
        },
      },
    });
  });

  /** @type {Object.<string, any> | undefined} */
  const globalEventsConfig = (() => {
    const jsonNode = document.querySelector(`script[type="application/json"]#DataLayerGlobalEventsConfigJSON`);
    if (!jsonNode) return;

    const textContent = jsonNode.textContent?.trim();
    if (!textContent) return;

    try {
      return JSON.parse(textContent);
    } catch (error) {
      console.error(error);
      return;
    }
  })();

  if (!globalEventsConfig) {
    console.error('Global events config for data layer is not defined');
    return;
  }

  if (globalEventsConfig.outboundClickEnabled) {
    /**
     * @param {HTMLAnchorElement} link
     * @param {string} outboundLinkType
     */
    const pushOutboundClick = (link, outboundLinkType) => {
      try {
        const url = new URL(link.href);
        if (url.host !== window.location.host) {
          window.dataLayerPush({
            event: 'outbound_click',
            data: {
              timestamp: new Date().toISOString(),
              data: {
                linkName: link.textContent?.trim() || undefined,
                linkUrl: link.href,
                outboundLinkType,
              },
            },
          });
        }
      } catch {
        // invalid URL - ignore
      }
    };

    document.addEventListener(
      'click',
      function (e) {
        /** @type {HTMLElement | null} */
        const target = e.target instanceof HTMLElement ? e.target : null;
        /** @type {HTMLAnchorElement | null | undefined} */
        const link = target?.closest('a[href]');
        if (!link) return;

        let outboundLinkType;
        if (target?.closest('nav')) {
          outboundLinkType = 'Navigation';
        } else if (target?.closest('footer')) {
          outboundLinkType = 'Footer';
        } else {
          outboundLinkType = 'Body';
        }

        pushOutboundClick(link, outboundLinkType);
      },
      true
    );

    // Diageo footer links
    const footerHost = document.querySelector('#diageo-footer-wrap');

    if (footerHost?.shadowRoot) {
      footerHost.shadowRoot.addEventListener(
        'click',
        function (e) {
          /** @type {HTMLElement | null} */
          const target = e.target instanceof HTMLElement ? e.target : null;

          /** @type {HTMLAnchorElement | null | undefined} */
          const link = target?.closest('a[href]');
          if (!link) return;

          pushOutboundClick(link, 'Footer');
        },
        true
      );
    }
  }

  if (globalEventsConfig.navigationClickEnabled && globalEventsConfig.navigationClickSelector) {
    document.querySelectorAll(globalEventsConfig.navigationClickSelector).forEach(
      /** @param {HTMLLinkElement} navigationLink */ (navigationLink) => {
        navigationLink.addEventListener('click', () => {
          const target = new URL(navigationLink.href);
          const targetHost = target.host;

          if (targetHost != window.location.host) return;

          const targetPathname = target.pathname?.split('/')[1] || 'index';

          /** @type {Object.<string, any> | undefined} */
          const globalValues = (() => {
            const jsonNode = document.querySelector(`script[type="application/json"]#DataLayerGlobalValuesJSON`);
            if (!jsonNode) return;

            const textContent = jsonNode.textContent?.trim();
            if (!textContent) return;

            try {
              return JSON.parse(textContent);
            } catch (error) {
              console.error(error);
              return;
            }
          })();

          if (!globalValues) return;

          /** @type {Object.<string, any> | undefined} */
          let journeyTypeMap;
          /** @type {Object.<string, any> | undefined} */
          let navigationTypeMap;
          try {
            journeyTypeMap = JSON.parse(globalValues.journeyTypeMap);
            navigationTypeMap = JSON.parse(globalValues.navigationTypeMap);
          } catch (error) {
            console.error('[dataLayer] Invalid journey/navigation type map JSON', error);
            return;
          }

          const getTypeFromMap = (/** @type {Object.<string, any>} */ map, /** @type {string} */ targetPathname) => {
            for (const pageType of Object.keys(map)) {
              const pathnames = map[pageType].split(',');
              if (pathnames.includes(targetPathname)) {
                return pageType;
              }
            }
          };

          window.dataLayerPush({
            event: 'navigation_click',
            data: {
              timestamp: new Date().toISOString(),
              data: {
                journeyType: getTypeFromMap(journeyTypeMap, targetPathname) || 'Content',
                navigationType: getTypeFromMap(navigationTypeMap, targetPathname) || 'Content',
                interactionName: navigationLink.textContent.trim() || undefined,
              },
            },
          });
        });
      }
    );
  }

  if (globalEventsConfig.scrollEnabled) {
    /** @type {Object.<string, boolean>} */
    const scrollMilestones = {
      '25%': false,
      '50%': false,
      '75%': false,
      '100%': false,
    };

    function getScrollPercentage() {
      const scrollTop = window.scrollY || window.pageYOffset;

      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
      );

      const winHeight = window.innerHeight;
      const scrollable = docHeight - winHeight;

      if (scrollable <= 0) return 100;

      return Math.round((scrollTop / scrollable) * 100);
    }

    function handleScroll() {
      const percentage = getScrollPercentage();

      Object.keys(scrollMilestones).forEach((milestone) => {
        if (!scrollMilestones[milestone] && percentage >= Number.parseInt(milestone)) {
          scrollMilestones[milestone] = true;
          window.dataLayerPush({
            event: 'scroll',
            data: {
              timestamp: new Date().toISOString(),
              data: {
                scrollPercentage: milestone,
              },
            },
          });
        }
      });
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
  }
})();
